import type { World } from '../sim/world';
import type { Vessel } from '../sim/vessel';
import type { SimEvent } from '../sim/events';
import type {
  AimPoint, CleanupKind, CollideScenario, CombatScenario, DestroyKind, DevAction, DevAi, DevCondition, DevFaction, FleetOrder, MergeOrder, SeaSpec,
} from '../sim/dev';
import { relWords } from '../sim/dev';
import { AMMO, DIFFICULTIES, SHIP_CLASSES, type AmmoId, type DifficultyId, type ShipClassId } from '../data/classes';
import { SAILS, SAIL_PLANS, type MastId } from '../data/ship';
import { BOARDING_GAP, GRAPNEL_REACH, GRAPNEL_SPEED, LINE_LENGTH, canGrapple, hullGap } from '../sim/contact';
import { hostile } from '../sim/captain';
import { KNOTS_PER_MS } from './nautical';

// Developer tools panel (Settings > Dev, or F2): the game's systems set off
// on request, grouped by category. Every tool is data: its parameters, a few
// presets, the buttons, and what each button sends to the sim (a DevAction,
// which travels in the PlayerCommand like any other order). A new test
// scenario is a new entry in CATEGORIES (and, if the sim needs to do
// something new, a new action in sim/dev.ts).

type Val = number | string | boolean;
type Values = Record<string, Val>;

interface SelectParam { kind: 'select'; key: string; label: string; options: readonly (readonly [string, string])[]; value: string }
interface RangeParam { kind: 'range'; key: string; label: string; min: number; max: number; step: number; value: number; fmt: (v: number) => string }
interface CheckParam { kind: 'check'; key: string; label: string; value: boolean }
type Param = SelectParam | RangeParam | CheckParam;

/** What a tool's buttons can reach. */
interface Ctx {
  world: World;
  us: Vessel;
  /** The ship the target picker points at (us, if there's nobody else). */
  target: Vessel;
  /** The pair a merge test is watching (else us and the target). */
  pair(): [number, number];
  ui: { openChart(): void; restart(): void };
}

interface ToolAction {
  label: string;
  style?: 'go' | 'danger';
  title?: string;
  /** What to send (nothing: the button did its work in the UI). */
  run(v: Values, ctx: Ctx): DevAction | DevAction[] | void;
}

interface Tool {
  title: string;
  hint?: string;
  /** Acts on the target picker's ship (its name is shown on the card). */
  targeted?: boolean;
  /** Its effects fall on us (the "since the last test" line follows our ship). */
  onUs?: boolean;
  params?: Param[];
  /** Quick settings: reset the parameters, apply these, and run the first button. */
  presets?: { label: string; title?: string; set: Values }[];
  actions: ToolAction[];
  /** Live readout under the buttons (HTML), refreshed a few times a second. */
  live?(ctx: Ctx, panel: DevPanel): string;
}

interface Category {
  id: string;
  title: string;
  blurb: string;
  tools: Tool[];
}

export interface DevPanelActions {
  send(a: DevAction): void;
  setGameSpeed(s: number): void;
  /** Point the camera at a ship (ours: back to the usual view). */
  watch(id: number): void;
  openChart(): void;
  restart(): void;
}

// ---------------------------------------------------------------- vocabulary

const sel = (key: string, label: string, options: readonly (readonly [string, string])[], value: string): SelectParam => ({ kind: 'select', key, label, options, value });
const rng = (key: string, label: string, min: number, max: number, step: number, value: number, fmt: (v: number) => string): RangeParam => ({ kind: 'range', key, label, min, max, step, value, fmt });
const chk = (key: string, label: string, value: boolean): CheckParam => ({ kind: 'check', key, label, value });

const num = (v: Values, k: string, or = 0): number => (Number.isFinite(Number(v[k])) && v[k] !== '' && v[k] !== undefined ? Number(v[k]) : or);
const str = (v: Values, k: string): string => String(v[k]);
const rad = (deg: number): number => (deg * Math.PI) / 180;
const toMs = (knots: number): number => knots / KNOTS_PER_MS;

const fm = (v: number) => `${Math.round(v)} m`;
const fm1 = (v: number) => `${v.toFixed(1)} m`;
const fm2 = (v: number) => `${v.toFixed(2)} m`;
const fkn = (v: number) => `${v.toFixed(1)} kn`;
const fskn = (v: number) => `${v > 0 ? '+' : ''}${v.toFixed(2)} kn`;
const fsm = (v: number) => `${v > 0 ? '+' : ''}${Math.round(v)} m`;
const fdeg = (v: number) => `${v > 0 ? '+' : ''}${Math.round(v)}°`;
const fpct = (v: number) => `${Math.round(v)}%`;
const fint = (v: number) => `${Math.round(v)}`;
const fs = (v: number) => `${v.toFixed(1)} s`;
const fpow = (v: number) => v.toFixed(2);
const fx = (v: number) => `×${v.toFixed(1)}`;

const CLASSES = Object.values(SHIP_CLASSES).map((c) => [c.id, c.name] as const);
const FACTIONS = [['pirate', 'Pirate'], ['navy', 'Navy'], ['merchant', 'Merchant']] as const;
const SECTIONS = [['4', 'Bow'], ['3', 'Forward'], ['2', 'Waist'], ['1', 'Aft'], ['0', 'Stern']] as const;
const SIDES = [['1', 'Starboard'], ['-1', 'Port']] as const;
const BEARINGS = [['0', 'Dead ahead'], ['45', 'Starboard bow'], ['90', 'Starboard beam'], ['135', 'Starboard quarter'], ['180', 'Astern'], ['-135', 'Port quarter'], ['-90', 'Port beam'], ['-45', 'Port bow']] as const;
const AMMOS = Object.values(AMMO).map((a) => [a.id, a.name] as const);
const GUNS = [['0.5', '6-pounder'], ['1', '12-pounder'], ['2', '24-pounder'], ['2.67', '32-pounder']] as const;
const MASTS_OPT = [['fore', 'Foremast'], ['main', 'Mainmast'], ['mizzen', 'Mizzen']] as const;
const DIFFS = Object.values(DIFFICULTIES).map((d) => [d.id, d.name] as const);
const PLANS = SAIL_PLANS.map((p, i) => [String(i), p.name] as const);
const AIS = [
  ['off', 'Nobody (holds her course, never fires)'], ['patrol', 'Her own business'], ['fight', 'Knows where we are: fights'],
  ['board', 'Comes to board us'], ['ram', 'Comes to ram us'], ['flee', 'Runs from us'], ['hold', 'Holds her course and fire'],
] as const;
const CONDITIONS = [
  ['fresh', 'Fresh'], ['battered', 'Battered (hull, holes, canvas, men)'], ['crippled', 'Crippled (dismasted, rudder, flooding)'],
  ['struck', 'Struck'], ['burning', 'On fire'], ['sinking', 'Sinking'],
] as const;
const AIMS = [
  ['waterline', 'Waterline'], ['hull', 'Side (between wind and water)'], ['rail', 'Rail & gun deck'], ['deck', 'Deck'],
  ['mast', 'Mast'], ['rigging', 'Rigging & canvas'], ['rudder', 'Rudder'],
] as const;
const FROMS = [
  ['90', 'Off her starboard beam'], ['-90', 'Off her port beam'], ['0', 'Ahead of her (raking)'], ['180', 'Astern of her (raking)'],
  ['45', 'On her starboard bow'], ['-45', 'On her port bow'], ['135', 'On her starboard quarter'], ['-135', 'On her port quarter'],
] as const;
const SCENARIOS: readonly (readonly [CombatScenario, string])[] = [
  ['duel', 'Duel: they close to fight'], ['broadside', 'Broadside to broadside, abeam'], ['boardUs', 'They come to board us'],
  ['ramUs', 'They come to ram us'], ['chase', 'Chase: they run, we pursue'], ['hunted', 'Hunted: they come up astern'],
  ['squadron', 'A squadron in company'], ['ambush', 'Ambush from both beams'], ['convoy', 'A convoy and its escort'],
  ['melee', 'Free-for-all: pirates and navy'],
];
const SAIL_OPTS = [['all', 'Every sail'], ...SAILS.map((s) => [s.id, s.name] as const)] as const;

const SEA_PRESETS: { label: string; title: string; spec: Omit<SeaSpec, 'now'> }[] = [
  { label: 'Glassy calm', title: 'A breath of air, no sea to speak of', spec: { wind: 0.5, windRel: null, scale: 0.05, swell: 0.05, chop: 0.2 } },
  { label: 'Gentle', title: 'Light breeze, half-metre waves', spec: { wind: 4, windRel: null, scale: 0.6, swell: 0.5, chop: 0.5 } },
  { label: 'Moderate', title: 'Moderate breeze, seas about 1.5 m', spec: { wind: 8, windRel: null, scale: 1, swell: 1, chop: 0.7 } },
  { label: 'Rough', title: 'Strong breeze, seas about 3.5 m, sharp crests', spec: { wind: 12, windRel: null, scale: 1, swell: 2, chop: 0.85 } },
  { label: 'Heavy swell', title: 'Light air over a long 4 m swell', spec: { wind: 4, windRel: null, scale: 0.4, swell: 4, chop: 0.4 } },
  { label: 'Storm sea', title: 'A gale and a breaking sea, about 7 m (as the storm weather has it)', spec: { wind: 20, windRel: null, scale: 1, swell: 3, chop: 1 } },
];

const mergeOrder = (order: MergeOrder) => (_: Values, c: Ctx): DevAction => {
  const [a, b] = c.pair();
  return { kind: 'mergeOrder', order, a, b };
};
const destroy = (what: DestroyKind) => (v: Values, c: Ctx): DevAction => ({
  kind: 'destroy', target: c.target.id, what, mast: (v.mast ?? 'main') as MastId, level: num(v, 'level'),
  side: (num(v, 'side', 1) < 0 ? -1 : 1) as 1 | -1, section: num(v, 'section', 2),
});
const cleanup = (what: CleanupKind, onTarget = false) => (_: Values, c: Ctx): DevAction => ({ kind: 'cleanup', what, target: onTarget ? c.target.id : c.us.id });
const order = (o: FleetOrder) => (v: Values, c: Ctx): DevAction => ({ kind: 'orders', who: str(v, 'who') === 'target' ? c.target.id : (str(v, 'who') as 'dev' | 'all'), order: o });

// ---------------------------------------------------------------- the tools

const CATEGORIES: Category[] = [
  {
    id: 'merge',
    title: 'Boat merging',
    blurb: 'Two ships brought together and made fast: grapnels across, the lines hauled in until the hulls touch, then the fight across the rails (between friends, they simply lie alongside). Set a pair up exactly, drive it with the controls, and watch every stage in the readout.',
    tools: [
      {
        title: 'Set up a pair',
        hint: 'A new ship placed beside the boarder (us, or another new ship ahead of us) at the gap, angle and speeds given.',
        params: [
          sel('pair', 'Boarder', [['us', 'Us'], ['them', 'A new ship (two new ships)']], 'us'),
          sel('bcls', 'New boarder: class', CLASSES, 'frigate'),
          sel('bfac', 'New boarder: colours', FACTIONS, 'pirate'),
          sel('cls', 'Her class', CLASSES, 'raider'),
          sel('fac', 'Her colours', FACTIONS, 'pirate'),
          sel('side', "She lies to the boarder's", SIDES, '1'),
          rng('gap', 'Water between hulls', 0.5, 40, 0.5, 8, fm1),
          rng('offset', 'Her centre ahead / astern', -30, 30, 1, 0, fsm),
          rng('angle', 'Converging angle', -45, 45, 1, 0, fdeg),
          sel('speed', "Boarder's speed", [['keep', 'Keep her way'], ['0', 'Stopped'], ['2', '2 kn'], ['4', '4 kn'], ['6', '6 kn'], ['8', '8 kn']], 'keep'),
          rng('rel', 'Her speed over his', -6, 6, 0.25, 0, fskn),
          rng('closing', 'Drifting in across', 0, 4, 0.1, 0, fkn),
          sel('state', 'Her state', [['active', 'Fighting'], ['struck', 'Struck (a prize)']], 'active'),
          rng('crew', 'Her people aboard', 10, 100, 5, 100, fpct),
          rng('morale', 'Her morale', 0, 100, 5, 75, fpct),
          sel('bmorale', "Boarder's morale", [['-1', 'As it is'], ['1', '100%'], ['0.5', '50%'], ['0.1', '10% (about to break)']], '-1'),
          sel('ai', 'Her captain', [['off', 'Nobody (holds her course)'], ['fight', 'Fights'], ['flee', 'Runs'], ['board', 'Boards the boarder']], 'off'),
          sel('grapple', 'Grapnels', [['none', 'Not yet (controls below)'], ['order', 'Order to board (rules apply)'], ['force', 'Made fast at once']], 'none'),
        ],
        presets: [
          { label: 'Ready to board', title: 'An enemy raider 8 m off, matching our way: press B, or "Order to board"', set: { gap: 8 } },
          { label: 'Grapple & fight', title: 'Made fast at 8 m: watch the haul, then the fight', set: { gap: 8, grapple: 'force' } },
          { label: 'Out of reach', title: `22 m of water: grapnels reach ${GRAPNEL_REACH} m, so the order can't be carried out`, set: { gap: 22, grapple: 'order' } },
          { label: 'Too fast', title: '6 kn between us: the lines would only part', set: { gap: 8, rel: 6, grapple: 'order' } },
          { label: 'Converging 20°', title: 'Coming together at an angle, drifting in at 1 kn', set: { gap: 18, angle: 20, closing: 1 } },
          { label: 'Struck prize', title: 'She has struck: boarders just take possession', set: { gap: 6, state: 'struck', grapple: 'order' } },
          { label: 'Big and small', title: 'A navy warship grapples a pirate raider (two new ships)', set: { pair: 'them', bcls: 'warship', bfac: 'navy', cls: 'raider', fac: 'pirate', gap: 6, speed: '4', grapple: 'force' } },
          { label: 'They board us', title: 'A raider 30 m off whose captain means to board us', set: { gap: 30, ai: 'board' } },
          { label: 'Pirate takes a merchant', title: 'Two new ships: a raider ordered to board a merchantman', set: { pair: 'them', bcls: 'raider', bfac: 'pirate', cls: 'merchant', fac: 'merchant', gap: 10, speed: '4', grapple: 'order' } },
          { label: 'Friends lashed', title: 'Two navy ships made fast: they lie alongside without a fight', set: { pair: 'them', bcls: 'frigate', bfac: 'navy', cls: 'schooner', fac: 'navy', gap: 6, speed: '4', grapple: 'force' } },
          { label: 'We lose', title: 'A navy frigate in fine heart against our shaken people', set: { cls: 'frigate', fac: 'navy', morale: 100, bmorale: '0.1', gap: 6, grapple: 'force' } },
        ],
        actions: [{
          label: 'Set up', style: 'go',
          run: (v) => ({ kind: 'merge', spec: {
            pair: str(v, 'pair') as 'us' | 'them', boarderClass: str(v, 'bcls') as ShipClassId, boarderFaction: str(v, 'bfac') as DevFaction,
            cls: str(v, 'cls') as ShipClassId, faction: str(v, 'fac') as DevFaction, side: num(v, 'side') < 0 ? -1 : 1,
            gap: num(v, 'gap'), offset: num(v, 'offset'), angle: rad(num(v, 'angle')),
            speed: str(v, 'speed') === 'keep' ? null : toMs(num(v, 'speed')), relSpeed: toMs(num(v, 'rel')), closing: toMs(num(v, 'closing')),
            state: str(v, 'state') as 'active' | 'struck', crew: num(v, 'crew') / 100, morale: num(v, 'morale') / 100,
            boarderMorale: num(v, 'bmorale'), ai: str(v, 'ai') as 'off' | 'fight' | 'flee' | 'board', grapple: str(v, 'grapple') as 'none' | 'order' | 'force',
          } }),
        }],
      },
      {
        title: 'Controls',
        hint: 'On the pair being watched (or on us and the target, if none has been set up).',
        actions: [
          { label: 'Order to board (B)', title: 'The ordinary order: grapnels go across only within reach and slow enough', run: mergeOrder('order') },
          { label: 'Grapple now', style: 'go', title: `Make fast at once, whatever the rules (up to ${LINE_LENGTH} m of water)`, run: mergeOrder('grapple') },
          { label: 'Cut the lines', run: mergeOrder('cut') },
          { label: 'Tear them apart', title: `Throw her clear at 4 m/s: the lines part past ${LINE_LENGTH} m`, run: mergeOrder('tear') },
          { label: 'She strikes', run: mergeOrder('strike') },
          { label: 'Take her now', run: mergeOrder('capture') },
          { label: 'Cut down half her men', style: 'danger', run: mergeOrder('weaken') },
          { label: 'Boarder rallies', title: "The boarder's morale to 100%", run: mergeOrder('rally') },
          { label: 'She loses heart', title: 'Her morale to 10%', run: mergeOrder('dishearten') },
        ],
        live: mergeLive,
      },
    ],
  },
  {
    id: 'ships',
    title: 'Ships',
    blurb: 'Put ships on the water anywhere round us: any class and colours, any course and speed, under any orders and in any state. The newest one becomes the target.',
    tools: [{
      title: 'Put a ship on the water',
      params: [
        sel('cls', 'Class', CLASSES, 'frigate'),
        sel('fac', 'Colours', FACTIONS, 'pirate'),
        sel('bearing', 'Where (from our bow)', BEARINGS, '45'),
        rng('range', 'Range', 60, 6000, 10, 600, fm),
        sel('course', 'Her course', [['relative', 'Relative to ours (below)'], ['toward', 'Toward us'], ['away', 'Away from us']], 'relative'),
        rng('turn', 'Her heading vs ours', -180, 180, 5, 0, fdeg),
        rng('speed', 'Speed', 0, 10, 0.5, 4, fkn),
        sel('plan', 'Sail', PLANS, '3'),
        sel('ai', 'Orders', AIS, 'off'),
        sel('cond', 'State', CONDITIONS, 'fresh'),
        rng('crew', 'People aboard', 10, 100, 5, 100, fpct),
        rng('count', 'How many', 1, 6, 1, 1, fint),
        sel('form', 'In company', [['line', 'Line ahead'], ['abreast', 'Line abreast']], 'line'),
        rng('spacing', 'Spacing', 60, 600, 10, 160, fm),
        sel('diff', 'Captain', DIFFS, 'normal'),
      ],
      presets: [
        { label: 'Hulk ahead', title: 'Lying stopped across our bow, 400 m off, nobody aboard in command', set: { bearing: '0', range: 400, speed: 0, plan: '0', turn: 90 } },
        { label: 'Frigate abeam', title: 'On our course 300 m to starboard, holding it', set: { bearing: '90', range: 300, speed: 4 } },
        { label: 'Enemy closing', title: 'A navy frigate 1.5 km off, coming to fight', set: { bearing: '45', range: 1500, fac: 'navy', ai: 'fight', course: 'toward' } },
        { label: 'Convoy', title: 'Three merchantmen in line ahead, about their business', set: { bearing: '-45', range: 1200, cls: 'merchant', fac: 'merchant', count: 3, ai: 'patrol', spacing: 220 } },
        { label: 'Battered raider', set: { bearing: '90', range: 400, cls: 'raider', cond: 'battered' } },
        { label: 'Burning', set: { bearing: '0', range: 500, cls: 'merchant', fac: 'merchant', cond: 'burning', speed: 0, plan: '0' } },
        { label: 'Sinking', set: { bearing: '90', range: 350, cond: 'sinking', speed: 1, plan: '0' } },
        { label: 'Struck prize', set: { bearing: '135', range: 300, cls: 'schooner', cond: 'struck' } },
      ],
      actions: [{
        label: 'Put her on the water', style: 'go',
        run: (v) => ({ kind: 'spawn', spec: {
          cls: str(v, 'cls') as ShipClassId, faction: str(v, 'fac') as DevFaction, bearing: rad(num(v, 'bearing')), range: num(v, 'range'),
          course: str(v, 'course') as 'toward' | 'away' | 'relative', turn: rad(num(v, 'turn')), speed: toMs(num(v, 'speed')), plan: num(v, 'plan'),
          ai: str(v, 'ai') as DevAi, condition: str(v, 'cond') as DevCondition, crew: num(v, 'crew') / 100, count: num(v, 'count'),
          formation: str(v, 'form') as 'line' | 'abreast', spacing: num(v, 'spacing'), difficulty: str(v, 'diff') as DifficultyId,
        } }),
      }],
      live: shipsLive,
    }],
  },
  {
    id: 'combat',
    title: 'Combat',
    blurb: 'Enemies that already know where we are: the fight starts now, not when somebody happens to sight somebody.',
    tools: [
      {
        title: 'Start a fight',
        params: [
          sel('scenario', 'Situation', SCENARIOS, 'duel'),
          sel('cls', 'Their class', CLASSES, 'frigate'),
          sel('fac', 'Their colours', FACTIONS, 'navy'),
          rng('count', 'Ships', 1, 4, 1, 1, fint),
          rng('range', 'Range', 200, 3000, 50, 900, fm),
          sel('diff', 'Captains', DIFFS, 'normal'),
          chk('quarters', 'We beat to quarters at once', true),
        ],
        presets: [
          { label: 'Duel', set: { scenario: 'duel' } },
          { label: 'Broadsides', set: { scenario: 'broadside', range: 400 } },
          { label: 'Boarders', set: { scenario: 'boardUs', cls: 'raider', fac: 'pirate', range: 600 } },
          { label: 'Rammer', set: { scenario: 'ramUs', cls: 'warship', fac: 'pirate', range: 600 } },
          { label: 'Chase', set: { scenario: 'chase', cls: 'merchant', fac: 'merchant', range: 800 } },
          { label: 'Hunted', set: { scenario: 'hunted', count: 2, range: 1200 } },
          { label: 'Squadron', set: { scenario: 'squadron', cls: 'warship', count: 2, range: 1500 } },
          { label: 'Ambush', set: { scenario: 'ambush', cls: 'schooner', fac: 'pirate', count: 2, range: 700 } },
          { label: 'Convoy', set: { scenario: 'convoy', count: 2, range: 1400 } },
          { label: 'Free-for-all', set: { scenario: 'melee', count: 2, range: 1000 } },
        ],
        actions: [{
          label: 'Start', style: 'go',
          run: (v) => ({ kind: 'combat', spec: {
            scenario: str(v, 'scenario') as CombatScenario, cls: str(v, 'cls') as ShipClassId, faction: str(v, 'fac') as DevFaction,
            count: num(v, 'count'), range: num(v, 'range'), difficulty: str(v, 'diff') as DifficultyId, quarters: v.quarters === true,
          } }),
        }],
      },
      {
        title: 'Orders',
        params: [sel('who', 'To', [['dev', 'The test ships'], ['all', 'Every other ship'], ['target', 'The target']], 'dev')],
        actions: [
          { label: 'Fight us', run: order('fight') },
          { label: 'Board us', run: order('board') },
          { label: 'Hold fire', run: order('hold') },
          { label: 'Run', run: order('flee') },
          { label: 'Strike', run: order('strike') },
          { label: 'Own judgement', run: order('release') },
        ],
        live: captainsLive,
      },
    ],
  },
  {
    id: 'shells',
    title: 'Shells',
    blurb: 'Every kind of shot, from a gun standing on the water, laid on any part of the target (drag, drop, wind and her way allowed for). Hits are worked out against her real shape; the report below and the log say what each round did.',
    tools: [
      {
        title: 'Fire at the target',
        targeted: true,
        params: [
          sel('ammo', 'Shot', AMMOS, 'round'),
          sel('gun', 'Gun', GUNS, '1'),
          sel('aim', 'Laid on her', AIMS, 'waterline'),
          sel('section', 'Along her', SECTIONS, '2'),
          sel('from', 'The gun lies', FROMS, '90'),
          rng('range', 'Range', 30, 1300, 10, 300, fm),
          sel('height', 'Gun height', [['4', 'Gun deck (4 m)'], ['12', 'Quarterdeck (12 m)'], ['20', 'Fighting top (20 m)'], ['80', 'High above (80 m): plunging'], ['200', 'Very high (200 m)']], '4'),
          rng('count', 'Rounds', 1, 20, 1, 1, fint),
          rng('interval', 'Between rounds', 0, 3, 0.1, 0.5, fs),
        ],
        presets: [
          { label: 'Round, waterline', set: { ammo: 'round', aim: 'waterline' } },
          { label: 'Chain, rigging', set: { ammo: 'chain', aim: 'rigging', range: 250 } },
          { label: 'Grape, gun deck', set: { ammo: 'grape', aim: 'rail', range: 120 } },
          { label: 'Shell, side', set: { ammo: 'shell', aim: 'hull' } },
          { label: 'Rake her stern', set: { aim: 'rail', from: '180', section: '0', range: 150 } },
          { label: 'Rake her bow', set: { aim: 'rail', from: '0', section: '4', range: 150 } },
          { label: 'Plunging fire', set: { aim: 'deck', height: '200', range: 120 } },
          { label: 'Rudder', set: { aim: 'rudder', from: '180', range: 100 } },
          { label: 'Mast', set: { aim: 'mast', range: 200 } },
          { label: 'Broadside ×10', set: { count: 10, interval: 0.2 } },
        ],
        actions: [{
          label: 'Fire', style: 'go',
          run: (v, c) => ({ kind: 'fire', spec: {
            target: c.target.id, ammo: str(v, 'ammo') as AmmoId, weight: num(v, 'gun', 1), aim: str(v, 'aim') as AimPoint, section: num(v, 'section'),
            from: rad(num(v, 'from')), range: num(v, 'range'), count: num(v, 'count'), interval: num(v, 'interval'), height: num(v, 'height', 4),
          } }),
        }],
        live: shotLive,
      },
      {
        title: 'Our guns',
        hint: 'Every gun of ours loaded at once, and a broadside if you like (it goes as the gun crews come up).',
        params: [sel('ammo', 'Load with', AMMOS, 'round')],
        actions: [
          { label: 'Load every gun', run: (v) => ({ kind: 'battery', ammo: str(v, 'ammo') as AmmoId, side: 0 }) },
          { label: 'Load & fire port', run: (v) => ({ kind: 'battery', ammo: str(v, 'ammo') as AmmoId, side: -1 }) },
          { label: 'Load & fire starboard', run: (v) => ({ kind: 'battery', ammo: str(v, 'ammo') as AmmoId, side: 1 }) },
        ],
      },
      { title: 'The shot', actions: [], live: ammoTable },
    ],
  },
  {
    id: 'explosions',
    title: 'Explosions',
    blurb: "Blasts aboard use the ship's own explosion model: planking, decks and frames, fire, the men nearby, masts. In the water, a shock stoves in hulls near it, heaves them up and kills swimmers.",
    tools: [
      {
        title: 'Aboard the target',
        targeted: true,
        params: [
          sel('section', 'Where', SECTIONS, '2'),
          sel('level', 'Deck', [['deck', 'On deck'], ['hold', 'In the hold'], ['rail', 'At the rail']], 'deck'),
          sel('side', 'Side', [['0', 'Centreline'], ['1', 'Starboard'], ['-1', 'Port']], '0'),
          rng('power', 'Power', 0.25, 8, 0.25, 1, fpow),
          rng('count', 'Times', 1, 10, 1, 1, fint),
          rng('interval', 'Between', 0, 5, 0.1, 1, fs),
        ],
        presets: [
          { label: 'Shell burst (1)', set: { power: 1 } },
          { label: 'Powder keg (2.5)', set: { power: 2.5 } },
          { label: 'Magazine (6)', set: { power: 6, level: 'hold' } },
          { label: 'Chain of three', set: { power: 1.5, count: 3, interval: 0.8, section: '3' } },
        ],
        actions: [{
          label: 'Set it off', style: 'danger',
          run: (v, c) => ({ kind: 'explode', spec: {
            where: 'ship', target: c.target.id, section: num(v, 'section'), level: str(v, 'level') as 'hold' | 'deck' | 'rail',
            side: num(v, 'side') as -1 | 0 | 1, distance: 0, bearing: 0, power: num(v, 'power'), count: num(v, 'count'), interval: num(v, 'interval'),
          } }),
        }],
      },
      {
        title: 'In the water off her side',
        targeted: true,
        params: [
          sel('side', 'Side', SIDES, '1'),
          sel('section', 'Abreast of', SECTIONS, '2'),
          rng('distance', 'Off her side', 0, 40, 0.5, 4, fm1),
          rng('power', 'Power', 0.25, 8, 0.25, 3, fpow),
          rng('count', 'Times', 1, 10, 1, 1, fint),
          rng('interval', 'Between', 0, 5, 0.1, 1, fs),
        ],
        presets: [
          { label: 'Mine against her', set: { distance: 0.5, power: 3 } },
          { label: 'Near miss', set: { distance: 12, power: 1 } },
          { label: 'Big one close', set: { distance: 6, power: 6 } },
        ],
        actions: [{
          label: 'Set it off', style: 'danger',
          run: (v, c) => ({ kind: 'explode', spec: {
            where: 'alongside', target: c.target.id, section: num(v, 'section'), level: 'deck', side: num(v, 'side') < 0 ? -1 : 1,
            distance: num(v, 'distance'), bearing: 0, power: num(v, 'power'), count: num(v, 'count'), interval: num(v, 'interval'),
          } }),
        }],
      },
      {
        title: 'At sea, off our bow',
        onUs: true,
        params: [
          sel('bearing', 'Bearing', BEARINGS, '45'),
          rng('distance', 'Range', 10, 1500, 5, 80, fm),
          rng('power', 'Power', 0.25, 8, 0.25, 2, fpow),
          rng('count', 'Times', 1, 10, 1, 1, fint),
          rng('interval', 'Between', 0, 5, 0.1, 1, fs),
        ],
        actions: [{
          label: 'Set it off', style: 'danger',
          run: (v, c) => ({ kind: 'explode', spec: {
            where: 'sea', target: c.us.id, section: 2, level: 'deck', side: 0, distance: num(v, 'distance'), bearing: rad(num(v, 'bearing')),
            power: num(v, 'power'), count: num(v, 'count'), interval: num(v, 'interval'),
          } }),
        }],
      },
    ],
  },
  {
    id: 'collisions',
    title: 'Collisions',
    blurb: "Ships put on collision courses with nobody at the helm, timed to strike where you choose; the contact physics and the damage it deals do the rest. (Ship on ship is what collides here: there's no land, and flotsam only drifts.)",
    tools: [{
      title: 'Stage a collision',
      onUs: true,
      params: [
        sel('scenario', 'What', [
          ['ramUs', 'She rams our side'], ['glancing', 'A glancing blow down our side'], ['headOn', 'Head-on, bow to bow'],
          ['weRam', 'We ram her side'], ['pair', 'Two other ships collide'], ['bump', 'A gentle bump alongside'],
        ], 'ramUs'),
        sel('cls', 'Her class', CLASSES, 'frigate'),
        sel('fac', 'Her colours', FACTIONS, 'pirate'),
        rng('speed', 'Speed (a bump: closing)', 0.5, 12, 0.5, 6, fkn),
        sel('section', 'Struck at', SECTIONS, '2'),
        sel('side', 'Side struck', SIDES, '1'),
        rng('lead', 'Seconds to impact', 2, 15, 1, 5, fs),
      ],
      presets: [
        { label: 'T-bone, 8 kn', set: { scenario: 'ramUs', speed: 8 } },
        { label: 'Glancing', set: { scenario: 'glancing', speed: 5 } },
        { label: 'Head-on', set: { scenario: 'headOn', speed: 4 } },
        { label: 'We ram, 7 kn', set: { scenario: 'weRam', speed: 7 } },
        { label: 'Two others', set: { scenario: 'pair', speed: 7 } },
        { label: 'Bump, 1 kn', set: { scenario: 'bump', speed: 1 } },
        { label: 'Heavy into light', set: { scenario: 'ramUs', cls: 'warship', speed: 6 } },
      ],
      actions: [{
        label: 'Stage it', style: 'go',
        run: (v) => ({ kind: 'collide', spec: {
          scenario: str(v, 'scenario') as CollideScenario, cls: str(v, 'cls') as ShipClassId, faction: str(v, 'fac') as DevFaction,
          speed: toMs(num(v, 'speed')), section: num(v, 'section'), side: num(v, 'side') < 0 ? -1 : 1, lead: num(v, 'lead'),
        } }),
      }],
      live: collisionsLive,
    }],
  },
  {
    id: 'destruction',
    title: 'Destruction',
    blurb: 'Carry away, blow up or sink parts of the target, and watch the damage model react: rig and canvas come down, she floods, lists, burns and founders.',
    tools: [
      {
        title: 'Masts',
        targeted: true,
        params: [
          sel('mast', 'Mast', MASTS_OPT, 'main'),
          sel('level', 'Carry away', [['0', 'The lower mast'], ['1', 'The topmast'], ['2', 'The topgallant']], '0'),
          sel('side', 'Falls to', SIDES, '1'),
        ],
        actions: [{ label: 'Carry it away', style: 'danger', run: destroy('dismast') }],
      },
      {
        title: 'Hull',
        targeted: true,
        params: [sel('section', 'Where', SECTIONS, '2'), sel('side', 'Side', SIDES, '1')],
        actions: [
          { label: 'Stave in her side', style: 'danger', run: destroy('stove') },
          { label: 'Break her back', style: 'danger', run: destroy('back') },
          { label: 'Flood her', style: 'danger', title: 'Water past saving: she founders', run: destroy('sink') },
        ],
      },
      {
        title: 'Fire and powder',
        targeted: true,
        params: [sel('section', 'Where', SECTIONS, '2')],
        actions: [
          { label: 'Set her ablaze', style: 'danger', run: destroy('burn') },
          { label: 'Blow up her magazine', style: 'danger', run: destroy('magazine') },
        ],
      },
      {
        title: 'Fighting power',
        targeted: true,
        params: [sel('side', 'Guns on', SIDES, '1')],
        actions: [
          { label: 'Dismount her guns', run: destroy('guns') },
          { label: 'Shoot away her rudder', run: destroy('rudder') },
          { label: 'Shred her sails', run: destroy('sails') },
          { label: 'Cut down half her people', style: 'danger', run: destroy('crew') },
        ],
      },
      {
        title: 'Surrender',
        targeted: true,
        actions: [
          { label: 'Strike her colours', run: destroy('strike') },
          { label: 'Take her as a prize', run: destroy('capture') },
        ],
      },
    ],
  },
  {
    id: 'damage',
    title: 'Damage',
    blurb: 'Exact amounts of damage to single parts of the target, through the same damage model shot, fire and collisions use. Amounts are a share of the part when new.',
    tools: [
      {
        title: 'Hull planking', targeted: true,
        params: [sel('section', 'Section', SECTIONS, '2'), sel('side', 'Side', SIDES, '1'), rng('percent', 'Damage', 5, 150, 5, 25, fpct)],
        actions: [{ label: 'Apply', run: (v, c) => ({ kind: 'damage', target: c.target.id, spec: { part: 'hull', section: num(v, 'section'), side: num(v, 'side') < 0 ? -1 : 1, percent: num(v, 'percent') } }) }],
      },
      {
        title: 'Shot hole', targeted: true,
        params: [
          sel('section', 'Section', SECTIONS, '2'), sel('side', 'Side', SIDES, '1'),
          sel('height', 'Height', [['waterline', 'At the waterline'], ['below', 'Below the waterline'], ['above', 'Above the waterline']], 'waterline'),
          rng('radius', 'Radius', 0.03, 1.2, 0.01, 0.1, fm2),
        ],
        actions: [{ label: 'Hole her', run: (v, c) => ({ kind: 'damage', target: c.target.id, spec: { part: 'hole', section: num(v, 'section'), side: num(v, 'side') < 0 ? -1 : 1, height: str(v, 'height') as 'below' | 'waterline' | 'above', radius: num(v, 'radius') } }) }],
      },
      {
        title: 'Bulwarks', targeted: true,
        params: [sel('section', 'Section', SECTIONS, '2'), sel('side', 'Side', SIDES, '1'), rng('size', 'Breach', 0.3, 4, 0.1, 1, fx)],
        actions: [{ label: 'Stave in the rail', run: (v, c) => ({ kind: 'damage', target: c.target.id, spec: { part: 'rail', section: num(v, 'section'), side: num(v, 'side') < 0 ? -1 : 1, size: num(v, 'size') } }) }],
      },
      {
        title: 'Deck and frames', targeted: true,
        params: [sel('section', 'Section', SECTIONS, '2'), rng('deck', 'Deck', 0, 150, 5, 30, fpct), rng('frames', 'Frames', 0, 150, 5, 0, fpct)],
        actions: [{ label: 'Apply', run: (v, c) => ({ kind: 'damage', target: c.target.id, spec: { part: 'deck', section: num(v, 'section'), deck: num(v, 'deck'), frames: num(v, 'frames') } }) }],
      },
      {
        title: 'Mast and rigging', targeted: true,
        params: [sel('mast', 'Mast', MASTS_OPT, 'main'), rng('percent', 'Mast', 0, 150, 5, 40, fpct), rng('rigging', 'Standing rigging', 0, 100, 5, 0, fpct)],
        actions: [{ label: 'Apply', run: (v, c) => ({ kind: 'damage', target: c.target.id, spec: { part: 'mast', mast: str(v, 'mast') as MastId, percent: num(v, 'percent'), rigging: num(v, 'rigging') } }) }],
      },
      {
        title: 'Rudder', targeted: true,
        params: [rng('percent', 'Damage', 5, 100, 5, 50, fpct)],
        actions: [{ label: 'Apply', run: (v, c) => ({ kind: 'damage', target: c.target.id, spec: { part: 'rudder', percent: num(v, 'percent') } }) }],
      },
      {
        title: 'Sails', targeted: true,
        params: [sel('sail', 'Sail', SAIL_OPTS, 'all'), rng('radius', 'Tear (radius)', 0, 2.5, 0.1, 0.8, fm1), rng('burn', 'Burn away', 0, 100, 5, 0, fpct)],
        actions: [{ label: 'Apply', run: (v, c) => ({ kind: 'damage', target: c.target.id, spec: { part: 'sails', sail: str(v, 'sail'), radius: num(v, 'radius'), burn: num(v, 'burn') } }) }],
      },
      {
        title: 'Guns', targeted: true,
        params: [sel('side', 'Side', [['1', 'Starboard'], ['-1', 'Port'], ['0', 'Both']], '1'), rng('count', 'Dismount', 1, 20, 1, 2, fint)],
        actions: [{ label: 'Apply', run: (v, c) => ({ kind: 'damage', target: c.target.id, spec: { part: 'guns', side: num(v, 'side') as -1 | 0 | 1, count: num(v, 'count') } }) }],
      },
      {
        title: 'People', targeted: true,
        params: [rng('killed', 'Killed', 0, 60, 1, 5, fint), rng('wounded', 'Wounded', 0, 60, 1, 0, fint), rng('morale', 'Morale', -1, 100, 1, -1, (x) => (x < 0 ? 'as it is' : fpct(x)))],
        actions: [{ label: 'Apply', run: (v, c) => ({ kind: 'damage', target: c.target.id, spec: { part: 'crew', killed: num(v, 'killed'), wounded: num(v, 'wounded'), morale: num(v, 'morale') < 0 ? null : num(v, 'morale') / 100 } }) }],
      },
      {
        title: 'Flooding', targeted: true,
        params: [sel('section', 'Hold', SECTIONS, '2'), rng('fraction', 'Filled', 0, 100, 5, 50, fpct)],
        actions: [{ label: 'Flood it', run: (v, c) => ({ kind: 'damage', target: c.target.id, spec: { part: 'flood', section: num(v, 'section'), fraction: num(v, 'fraction') / 100 } }) }],
      },
      {
        title: 'Fire', targeted: true,
        params: [sel('section', 'Section', SECTIONS, '2'), sel('level', 'Deck', [['1', 'On deck'], ['0', 'In the hold']], '1'), rng('heat', 'Heat', 5, 100, 5, 50, fpct)],
        actions: [{ label: 'Light it', run: (v, c) => ({ kind: 'damage', target: c.target.id, spec: { part: 'fire', section: num(v, 'section'), level: num(v, 'level') ? 1 : 0, heat: num(v, 'heat') / 100 } }) }],
      },
      {
        title: 'Magazine', targeted: true, hint: 'At 100% heat she blows up.',
        params: [rng('heat', 'Add heat', 5, 100, 5, 25, fpct)],
        actions: [{ label: 'Heat it', style: 'danger', run: (v, c) => ({ kind: 'damage', target: c.target.id, spec: { part: 'magazine', heat: num(v, 'heat') / 100 } }) }],
      },
    ],
  },
  {
    id: 'sea',
    title: 'Water & waves',
    blurb: 'Set the wind and sea directly (normally they follow the weather) and see how the ships ride it. The sea keeps the direction it was running in when the game began (only its height follows the wind), so to take it on the bow, beam or quarter, turn her to meet it.',
    tools: [
      {
        title: 'Sea states',
        hint: 'Applied at once, wind and sea together.',
        actions: SEA_PRESETS.map((p) => ({ label: p.label, title: p.title, run: (): DevAction => ({ kind: 'sea', spec: { ...p.spec, now: true } }) })),
        live: seaLive,
      },
      {
        title: 'Meet the sea',
        hint: 'Turns our ship on the spot (heel, trim and way kept) so the sea comes from where you choose.',
        onUs: true,
        actions: [
          { label: 'Head sea', run: (): DevAction => ({ kind: 'face', rel: 0 }) },
          { label: 'On the bow', run: (): DevAction => ({ kind: 'face', rel: Math.PI / 4 }) },
          { label: 'Beam sea', run: (): DevAction => ({ kind: 'face', rel: Math.PI / 2 }) },
          { label: 'On the quarter', run: (): DevAction => ({ kind: 'face', rel: (3 * Math.PI) / 4 }) },
          { label: 'Following sea', run: (): DevAction => ({ kind: 'face', rel: Math.PI }) },
        ],
      },
      {
        title: 'Set the sea',
        hint: 'Leave a slider at the far left for what the weather gives. The wind can come from anywhere (the sails feel it at once); the sea builds only from its own quarter.',
        params: [
          rng('wind', 'Wind', -1, 60, 1, -1, (x) => (x < 0 ? 'weather' : `${Math.round(x)} kn`)),
          sel('from', 'Wind from', [['w', 'As the weather has it'], ['0', 'Ahead'], ['45', 'Starboard bow'], ['90', 'Starboard beam'], ['135', 'Starboard quarter'], ['180', 'Astern'], ['-135', 'Port quarter'], ['-90', 'Port beam'], ['-45', 'Port bow']], 'w'),
          rng('scale', 'Wind sea', -0.05, 2.5, 0.05, -0.05, (x) => (x < 0 ? 'weather' : `×${x.toFixed(2)}`)),
          rng('swell', 'Swell', -0.1, 6, 0.1, -0.1, (x) => (x < 0 ? 'weather' : `${x.toFixed(1)} m`)),
          rng('chop', 'Crest sharpness', -0.05, 1, 0.05, -0.05, (x) => (x < 0 ? 'weather' : fpct(x * 100))),
          chk('now', 'At once (else it builds up)', true),
        ],
        actions: [
          {
            label: 'Apply', style: 'go',
            run: (v) => ({ kind: 'sea', spec: {
              wind: num(v, 'wind') < 0 ? null : toMs(num(v, 'wind')), windRel: str(v, 'from') === 'w' ? null : rad(num(v, 'from')),
              scale: num(v, 'scale') < 0 ? null : num(v, 'scale'), swell: num(v, 'swell') < 0 ? null : num(v, 'swell'),
              chop: num(v, 'chop') < 0 ? null : num(v, 'chop'), now: v.now === true,
            } }),
          },
          { label: 'Back to the weather', run: cleanup('sea') },
        ],
      },
    ],
  },
  {
    id: 'cleanup',
    title: 'Reset & cleanup',
    blurb: 'Take away what the tests left behind, make ships good, or start over.',
    tools: [
      {
        title: 'Clear away',
        actions: [
          { label: 'Remove test ships', style: 'go', run: cleanup('dev') },
          { label: 'Remove every other ship', style: 'danger', run: cleanup('others') },
          { label: 'Clear shot & queued tests', run: cleanup('shot') },
          { label: 'Clear wreckage', run: cleanup('wreckage') },
          { label: 'Put out every fire', run: cleanup('fires') },
          { label: 'Sea back to the weather', run: cleanup('sea') },
        ],
        live: cleanupLive,
      },
      {
        title: 'Repair',
        hint: 'Hull, masts, canvas, guns, rudder, pumps, fires and the wounded, and her colours hoisted again. A ship on the bottom stays there; the dead stay dead.',
        actions: [
          { label: 'Repair the target', run: cleanup('repair', true) },
          { label: 'Repair us', style: 'go', run: cleanup('repair') },
        ],
      },
      {
        title: 'Start over',
        hint: 'Reset the test area: test ships, shot, wreckage and sea settings cleared, and our ship repaired. Restart: the whole game from the beginning.',
        actions: [
          { label: 'Reset the test area', style: 'go', run: cleanup('all') },
          { label: 'Restart the game', style: 'danger', run: (_, c) => c.ui.restart() },
        ],
      },
    ],
  },
  {
    id: 'chart',
    title: 'Chart',
    blurb: 'A chart of the sea round us with every ship on it (seen by our lookouts or not). Click anywhere on it to move our ship there, heading and way as she is.',
    tools: [{ title: 'Chart', actions: [{ label: 'Open the chart', style: 'go', run: (_, c) => c.ui.openChart() }] }],
  },
];

// ---------------------------------------------------------------- live readouts

const esc = (s: string): string => s.replace(/[&<>]/g, (c) => (c === '&' ? '&amp;' : c === '<' ? '&lt;' : '&gt;'));
const pc = (x: number): string => `${Math.round(Math.max(0, x) * 100)}%`;

function clock(t: number): string {
  const s = Math.max(0, Math.floor(t));
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}

function bar(x: number, cls = ''): string {
  const f = Math.max(0, Math.min(1, x));
  const tone = f > 0.66 ? 'hi' : f > 0.33 ? 'mid' : 'lo';
  return `<span class="dev-bar ${cls}"><i class="${tone}" style="width:${Math.round(f * 100)}%"></i></span>`;
}

function vessel(w: World, id: number): Vessel | undefined {
  return w.vessels.find((v) => v.id === id);
}

function mergeLive(c: Ctx): string {
  const w = c.world, m = w.dev.merge;
  const [ai, bi] = c.pair();
  const a = vessel(w, ai), b = vessel(w, bi);
  if (!a || !b || a === b) return '<span class="dim">No pair yet: set one up above, or pick a target to merge with us.</span>';
  const gap = hullGap(a, b);
  const dx = b.ship.com.x - a.ship.com.x, dz = b.ship.com.z - a.ship.com.z, d = Math.hypot(dx, dz) || 1;
  const rvx = b.ship.vel.x - a.ship.vel.x, rvz = b.ship.vel.z - a.ship.vel.z;
  const rel = Math.hypot(rvx, rvz), closing = -(rvx * dx + rvz * dz) / d;
  const linked = a.boardingWith === b.id && b.boardingWith === a.id;
  const foes = hostile(a, b) || hostile(b, a);
  const watching = m && m.a === a.id && m.b === b.id ? m : null;
  let status: string;
  if (watching && watching.ended >= 0) {
    const since = watching.lashed >= 0 ? ` ${Math.round(watching.ended - watching.lashed)} s after the lines went across` : '';
    status = `<b class="good">Over:</b> ${esc(watching.outcome)}${since}.`;
  } else if (linked && gap > BOARDING_GAP) {
    status = `<b>Grappled, hauling in:</b> ${(gap - BOARDING_GAP).toFixed(1)} m to go, closing at ${closing.toFixed(2)} m/s.`;
  } else if (linked) {
    status = foes ? '<b class="bad">Boarding fight</b> across the rails.' : '<b>Lashed alongside</b> (not enemies: no fight).';
  } else {
    const g = canGrapple(a, b);
    status = g.ok ? `<b>Not grappled.</b> Grapnels would take now.`
      : g.why === 'far' ? `<b>Not grappled.</b> Too far for grapnels (they reach ${GRAPNEL_REACH} m).`
      : g.why === 'fast' ? `<b>Not grappled.</b> Too fast: at most ${(GRAPNEL_SPEED * KNOTS_PER_MS).toFixed(1)} kn between them.`
      : `<b>Not grappled.</b> ${!a.fighting ? `${esc(a.name)} is out of the fight.` : `${esc(b.name)} is already a prize or grappled.`}`;
  }
  const hd = Math.abs(((((b.ship.heading - a.ship.heading) * 180) / Math.PI) % 360 + 540) % 360 - 180);
  const lost = (v: Vessel, base: number) => (watching ? ` · ${v.crew.killed - base} killed` : '');
  const row = (v: Vessel, base: number) =>
    `<div class="dev-kv"><span>${esc(v.name)}${v === w.player ? ' (us)' : ''}</span><span>${v.crew.fighters} fit on deck${lost(v, base)} · morale ${bar(v.crew.morale)} ${pc(v.crew.morale)} · ${v.state}</span></div>`;
  const times = watching
    ? [`set up ${clock(watching.start)}`, watching.lashed >= 0 ? `lines ${clock(watching.lashed)}` : '', watching.fight >= 0 ? `fight ${clock(watching.fight)}` : '', watching.ended >= 0 ? `over ${clock(watching.ended)}` : '']
      .filter(Boolean).join(' · ')
    : 'not being watched: set up a pair, or use the controls';
  return [
    `<div class="dev-status">${status}</div>`,
    `<div class="dev-kv"><span>Pair</span><span>${esc(a.name)} (${a.cls.name.toLowerCase()}) ⟷ ${esc(b.name)} (${b.cls.name.toLowerCase()})</span></div>`,
    `<div class="dev-kv"><span>Water between</span><span>${gap.toFixed(1)} m${watching ? ` · closest ${watching.minGap.toFixed(1)} m` : ''} · fight at ${BOARDING_GAP} m, lines part at ${LINE_LENGTH} m</span></div>`,
    `<div class="dev-kv"><span>Between them</span><span>${(rel * KNOTS_PER_MS).toFixed(1)} kn · closing ${closing.toFixed(2)} m/s · headings ${Math.round(hd)}° apart</span></div>`,
    row(a, watching?.killedA ?? 0),
    row(b, watching?.killedB ?? 0),
    watching ? `<div class="dev-kv"><span>Knocks</span><span>${watching.collisions} collision${watching.collisions === 1 ? '' : 's'}${watching.collisions ? `, hardest ${watching.hardest.toFixed(2)}` : ''} · lines parted ${watching.parted}×</span></div>` : '',
    `<div class="dev-kv"><span>Times</span><span>${times}</span></div>`,
  ].join('');
}

function shipsLive(c: Ctx): string {
  const w = c.world, me = w.player.ship;
  const list = w.vessels.filter((v) => w.dev.spawned.has(v.id));
  if (!list.length) return '<span class="dim">No test ships out.</span>';
  return list.map((v) => {
    const d = Math.hypot(v.ship.com.x - me.com.x, v.ship.com.z - me.com.z);
    const rel = Math.atan2(v.ship.com.x - me.com.x, -(v.ship.com.z - me.com.z)) - me.heading;
    const mode = v.state !== 'active' ? v.state : v.captain ? v.captain.mode : 'nobody in command';
    return `<div class="dev-kv"><span>${esc(v.name)}</span><span>${v.cls.name.toLowerCase()} · ${mode} · ${Math.round(d)} m ${relWords(rel)} · ${(v.ship.speed * KNOTS_PER_MS).toFixed(1)} kn</span></div>`;
  }).join('');
}

function captainsLive(c: Ctx): string {
  const w = c.world;
  const list = w.vessels.filter((v) => v !== w.player && v.alive).slice(0, 8);
  if (!list.length) return '<span class="dim">No other ships.</span>';
  return list.map((v) => {
    const cap = v.captain;
    const what = v.state !== 'active' ? v.state : !cap ? 'nobody in command' : `${cap.mode}${cap.forced ? ' (ordered)' : ''}: ${cap.intent || '…'}`;
    return `<div class="dev-kv"><span>${esc(v.name)}${w.dev.spawned.has(v.id) ? ' ·test' : ''}</span><span>${esc(what)}</span></div>`;
  }).join('');
}

function shotLive(_: Ctx, p: DevPanel): string {
  const r = p.shots;
  if (!r) return '<span class="dim">Fire, and what the shot does is reported here.</span>';
  const hits = [...r.hits.entries()].map(([k, n]) => `${k} ${n}`).join(', ');
  return [
    `<div class="dev-kv"><span>Last firing</span><span>${esc(r.label)} at ${esc(r.name)}, ${clock(r.t)}</span></div>`,
    `<div class="dev-kv"><span>Struck</span><span>${hits || 'nothing yet'}${r.hardest > 0 ? ` · hardest ${r.hardest.toFixed(2)}` : ''}</span></div>`,
    `<div class="dev-kv"><span>Misses</span><span>${r.splashes} splash${r.splashes === 1 ? '' : 'es'} near her</span></div>`,
    `<div class="dev-kv"><span>Aboard her</span><span>${r.killed} killed, ${r.wounded} wounded · ${r.explosions} burst${r.explosions === 1 ? '' : 's'} · ${r.fires} fire${r.fires === 1 ? '' : 's'} started</span></div>`,
  ].join('');
}

function ammoTable(): string {
  const rows = Object.values(AMMO).map((a) => `<tr><td>${a.name}</td><td>${a.speed}</td><td>${a.range}</td><td>${a.count}</td><td>${a.penetration}</td><td>${a.hull}/${a.sail}/${a.rig}/${a.crew}</td><td>${a.explosive ? 'yes' : ''}</td></tr>`);
  return `<table class="dev-table"><tr><th>Shot</th><th>m/s</th><th>range</th><th>balls</th><th>pierce</th><th>hull/sail/rig/men</th><th>bursts</th></tr>${rows.join('')}</table>`;
}

function collisionsLive(_: Ctx, p: DevPanel): string {
  if (!p.knocks.length) return '<span class="dim">No collisions yet.</span>';
  return p.knocks.slice(-5).reverse().map((k) => `<div class="dev-kv"><span>${clock(k.t)}</span><span>energy ${k.energy.toFixed(2)}${k.energy >= 1 ? ' (as hard as they come)' : ''}${k.near ? ` · by ${esc(k.near)}` : ''}</span></div>`).join('');
}

function seaLive(c: Ctx, p: DevPanel): string {
  const w = c.world, s = w.settings, me = w.player.ship;
  const over = [
    s.windSpeed !== null ? `wind ${(s.windSpeed * KNOTS_PER_MS).toFixed(0)} kn` : '', s.windFrom !== null ? 'wind direction' : '',
    s.seaScale !== null ? `wind sea ×${s.seaScale.toFixed(2)}` : '', s.swellHeight !== null ? `swell ${s.swellHeight.toFixed(1)} m` : '',
    s.chop !== null ? `chop ${pc(s.chop)}` : '',
  ].filter(Boolean);
  const from = relWords(w.wind.fromBearing - me.heading).replace(/^on the /, 'the ');
  return [
    `<div class="dev-kv"><span>Sea</span><span>${w.waves.significantHeight.toFixed(1)} m significant · peak wavelength ${Math.round(w.waves.peakWavelength)} m</span></div>`,
    `<div class="dev-kv"><span>Wind</span><span>${(w.wind.speed * KNOTS_PER_MS).toFixed(0)} kn from ${from}</span></div>`,
    `<div class="dev-kv"><span>Set by hand</span><span>${over.length ? over.join(', ') : 'nothing: all as the weather has it'}</span></div>`,
    `<div class="dev-kv"><span>${esc(w.player.name)}</span><span>rolling to ${p.motion.roll.toFixed(0)}°, pitching ${p.motion.pitch.toFixed(0)}° (last 20 s) · ${p.motion.slams} slams, ${p.motion.green} seas aboard (last minute) · deck water ${pc(me.deckWater)}</span></div>`,
  ].join('');
}

function cleanupLive(c: Ctx): string {
  const w = c.world;
  const others = w.vessels.filter((v) => v !== w.player).length;
  const wreck = w.flotsam.items.filter((f) => f.wreck !== undefined).length;
  let fires = 0;
  for (const v of w.vessels) fires += v.fire.burning;
  const set = [w.settings.windSpeed, w.settings.windFrom, w.settings.seaScale, w.settings.swellHeight, w.settings.chop].some((x) => x !== null);
  return `<div class="dev-kv"><span>On the water</span><span>${w.dev.spawned.size} test ship${w.dev.spawned.size === 1 ? '' : 's'} of ${others} other · ${w.ballistics.projectiles.length} shot in the air · ${wreck} pieces of wreckage · ${fires} fires burning · sea ${set ? 'set by hand' : 'as the weather has it'}</span></div>`;
}

// ---------------------------------------------------------------- the panel

function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, html?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (html !== undefined) e.innerHTML = html;
  return e;
}

/** A ship's state when a test was sent, for "since the last test". */
interface Snapshot {
  id: number;
  t: number;
  hull: number;
  holes: number;
  leak: number;
  killed: number;
  masts: number;
  sails: number;
  guns: number;
  fires: number;
  flooding: number;
}

function snapshot(v: Vessel, t: number): Snapshot {
  const d = v.damage;
  let hull = 0;
  for (const s of d.hull) for (const hp of s) hull += Math.max(0, hp);
  let masts = 0, n = 0;
  for (const m of Object.values(d.masts)) {
    masts += m.lostLevel >= 3 ? 0 : Math.max(0, m.hp) / m.max;
    n++;
  }
  return {
    id: v.id, t, hull: hull / (d.hullMax * 10), holes: d.holes.length, leak: d.leakArea, killed: v.crew.killed, masts: masts / Math.max(1, n),
    sails: v.ship.sails.sailHealth, guns: v.battery.guns.filter((g) => !g.destroyed).length, fires: v.fire.burning, flooding: v.ship.flooding,
  };
}

export class DevPanel {
  readonly el: HTMLElement;
  /** Report on the last firing from the Shells tool. */
  shots: { label: string; name: string; id: number; t: number; hits: Map<string, number>; hardest: number; splashes: number; killed: number; wounded: number; explosions: number; fires: number } | null = null;
  /** Recent collisions (for the Collisions readout). */
  readonly knocks: { t: number; energy: number; near: string }[] = [];
  /** Our ship's motion in the sea (for the Water readout). */
  readonly motion = { roll: 0, pitch: 0, slams: 0, green: 0 };
  private readonly body: HTMLElement;
  private readonly tabs = new Map<string, HTMLButtonElement>();
  private readonly speeds: { v: number; b: HTMLButtonElement }[] = [];
  private readonly targetSel: HTMLSelectElement;
  private readonly inspector: HTMLElement;
  private readonly logEl: HTMLElement;
  /** The newest log line, shown on the fold while the log is closed. */
  private readonly ticker: HTMLElement;
  private readonly footBtns: { insp: HTMLButtonElement; log: HTMLButtonElement };
  private readonly values = new Map<Tool, Values>();
  private lives: { tool: Tool; el: HTMLElement }[] = [];
  private cat = 'merge';
  /** The target picker: -2 is automatic (the newest test ship, else the nearest). */
  private pick = -2;
  private listSig = '';
  private timer = 0;
  private snap: Snapshot | null = null;
  private readonly rolls: { t: number; roll: number; pitch: number }[] = [];
  private readonly seaEvents: { t: number; kind: 'slam' | 'green' }[] = [];

  constructor(private readonly world: World, private readonly actions: DevPanelActions) {
    this.el = el('div', 'panel devpanel hidden');
    const head = el('div', 'dev-head');
    head.append(el('h2', '', 'Developer tools'));
    const speed = el('div', 'dev-speed');
    speed.title = 'Game speed';
    for (const [v, label] of [[0, '❚❚'], [0.25, '¼'], [0.5, '½'], [1, '1×'], [2, '2×'], [4, '4×']] as const) {
      const b = el('button', '', label);
      b.title = v === 0 ? 'Pause' : `Game speed ${label}`;
      b.addEventListener('click', () => {
        actions.setGameSpeed(v);
        b.blur();
      });
      speed.append(b);
      this.speeds.push({ v, b });
    }
    const close = el('button', 'dev-close', '×');
    close.title = 'Close (F2)';
    close.addEventListener('click', () => this.close());
    head.append(speed, close);

    const target = el('div', 'dev-target');
    target.append(el('span', 'dev-label', 'Target'));
    this.targetSel = el('select');
    this.targetSel.addEventListener('change', () => {
      this.pick = Number(this.targetSel.value);
      this.refresh();
    });
    const watch = el('button', '', 'Watch');
    watch.title = 'Camera on the target (any camera key to leave)';
    watch.addEventListener('click', () => {
      actions.watch(this.target().id);
      watch.blur();
    });
    target.append(this.targetSel, watch);

    const tabs = el('div', 'dev-tabs');
    for (const c of CATEGORIES) {
      const b = el('button', '', c.title);
      b.addEventListener('click', () => {
        this.cat = c.id;
        this.render();
        b.blur();
      });
      tabs.append(b);
      this.tabs.set(c.id, b);
    }
    this.body = el('div', 'dev-body');
    // Below: the target at a glance and the log, each folding away to give the tools room.
    const foot = el('div', 'dev-foot');
    const fold = el('div', 'dev-fold');
    const insp = el('button', 'active', 'Target');
    const log = el('button', '', 'Log');
    this.ticker = el('span', 'dev-ticker');
    fold.append(insp, log, this.ticker);
    this.footBtns = { insp, log };
    this.inspector = el('div', 'dev-insp');
    this.logEl = el('div', 'dev-log hidden');
    insp.addEventListener('click', () => this.fold('insp'));
    log.addEventListener('click', () => this.fold('log'));
    this.ticker.addEventListener('click', () => this.fold('log', true));
    foot.append(fold, this.inspector, this.logEl);
    this.el.append(head, target, tabs, this.body, foot);
    if (typeof window !== 'undefined' && window.innerHeight >= 900) this.fold('log', true);
    this.render();
  }

  /** Open or close the inspector or the log. */
  private fold(which: 'insp' | 'log', open?: boolean): void {
    const e = which === 'insp' ? this.inspector : this.logEl;
    const show = open ?? e.classList.contains('hidden');
    e.classList.toggle('hidden', !show);
    this.footBtns[which].classList.toggle('active', show);
    this.ticker.classList.toggle('hidden', which === 'log' && show);
    if (which === 'log' && show) this.logEl.scrollTop = this.logEl.scrollHeight;
  }

  get isOpen(): boolean {
    return !this.el.classList.contains('hidden');
  }

  open(): void {
    this.el.classList.remove('hidden');
    this.timer = 0;
  }

  close(): void {
    this.el.classList.add('hidden');
  }

  /** The ship the target picker points at. */
  target(): Vessel {
    if (this.pick >= 0) {
      const v = this.world.vessels.find((x) => x.id === this.pick && x.alive);
      if (v) return v;
    }
    return this.autoTarget();
  }

  /** The automatic choice: the newest test ship afloat, else the nearest ship, else us. */
  private autoTarget(): Vessel {
    const w = this.world;
    let best: Vessel | null = null;
    for (const v of w.vessels) if (w.dev.spawned.has(v.id) && v.alive && (!best || v.id > best.id)) best = v;
    if (best) return best;
    const me = w.player.ship.com;
    let bd = Infinity;
    for (const v of w.vessels) {
      if (v === w.player || !v.alive) continue;
      const d = Math.hypot(v.ship.com.x - me.x, v.ship.com.z - me.z);
      if (d < bd) (bd = d), (best = v);
    }
    return best ?? w.player;
  }

  private ctx(): Ctx {
    const w = this.world;
    return {
      world: w,
      us: w.player,
      target: this.target(),
      pair: () => {
        const m = w.dev.merge;
        if (m && vessel(w, m.a)?.alive && vessel(w, m.b)?.alive) return [m.a, m.b];
        return [w.player.id, this.target().id];
      },
      ui: { openChart: () => this.actions.openChart(), restart: () => this.actions.restart() },
    };
  }

  private valuesOf(tool: Tool): Values {
    let v = this.values.get(tool);
    if (!v) {
      v = {};
      for (const p of tool.params ?? []) v[p.key] = p.value;
      this.values.set(tool, v);
    }
    return v;
  }

  // ------------------------------------------------------------ building

  private render(): void {
    for (const [id, b] of this.tabs) b.classList.toggle('active', id === this.cat);
    const cat = CATEGORIES.find((c) => c.id === this.cat)!;
    this.body.innerHTML = '';
    this.lives = [];
    this.body.append(el('p', 'dev-blurb', cat.blurb));
    for (const t of cat.tools) this.body.append(this.card(t));
    this.body.scrollTop = 0;
    this.refresh();
  }

  private card(tool: Tool): HTMLElement {
    const card = el('div', 'dev-card');
    const h = el('h3', '', esc(tool.title));
    if (tool.targeted) h.append(el('span', 'dev-on'));
    card.append(h);
    if (tool.hint) card.append(el('p', 'dev-hint', esc(tool.hint)));
    const vals = this.valuesOf(tool);
    const setters: (() => void)[] = [];
    if (tool.presets?.length) {
      const row = el('div', 'dev-presets');
      for (const p of tool.presets) {
        const b = el('button', '', esc(p.label));
        if (p.title) b.title = p.title;
        b.addEventListener('click', () => {
          for (const q of tool.params ?? []) vals[q.key] = q.value;
          Object.assign(vals, p.set);
          for (const s of setters) s();
          this.run(tool, tool.actions[0]!, vals);
          b.blur();
        });
        row.append(b);
      }
      card.append(row);
    }
    for (const p of tool.params ?? []) {
      if (p.kind === 'check') {
        const row = el('label', 'dev-check');
        const input = el('input');
        input.type = 'checkbox';
        input.checked = vals[p.key] === true;
        input.addEventListener('change', () => (vals[p.key] = input.checked));
        setters.push(() => (input.checked = vals[p.key] === true));
        row.append(input, el('span', '', esc(p.label)));
        card.append(row);
        continue;
      }
      const row = el('label', 'dev-param');
      row.append(el('span', 'dev-label', esc(p.label)));
      if (p.kind === 'select') {
        const s = el('select');
        for (const [v, t] of p.options) {
          const o = el('option', '', esc(t));
          o.value = v;
          s.append(o);
        }
        s.value = String(vals[p.key]);
        s.addEventListener('change', () => (vals[p.key] = s.value));
        setters.push(() => (s.value = String(vals[p.key])));
        row.append(s);
      } else {
        const input = el('input');
        input.type = 'range';
        input.min = String(p.min);
        input.max = String(p.max);
        input.step = String(p.step);
        input.value = String(vals[p.key]);
        const out = el('span', 'dev-val', p.fmt(Number(vals[p.key])));
        input.addEventListener('input', () => {
          vals[p.key] = Number(input.value);
          out.textContent = p.fmt(Number(input.value));
        });
        setters.push(() => {
          input.value = String(vals[p.key]);
          out.textContent = p.fmt(Number(vals[p.key]));
        });
        row.append(input, out);
      }
      card.append(row);
    }
    if (tool.actions.length) {
      const row = el('div', 'dev-actions');
      for (const a of tool.actions) {
        const b = el('button', a.style ?? '', esc(a.label));
        if (a.title) b.title = a.title;
        b.addEventListener('click', () => {
          this.run(tool, a, vals);
          b.blur();
        });
        row.append(b);
      }
      card.append(row);
    }
    if (tool.live) {
      const live = el('div', 'dev-live');
      card.append(live);
      this.lives.push({ tool, el: live });
    }
    return card;
  }

  private run(tool: Tool, a: ToolAction, vals: Values): void {
    const c = this.ctx();
    const out = a.run(vals, c);
    if (!out) return;
    const list = Array.isArray(out) ? out : [out];
    // Keep the state of the ship tested on, to show what the test did to her.
    if (tool.targeted || tool.onUs) this.snap = snapshot(tool.targeted ? c.target : c.us, this.world.time);
    for (const x of list) {
      if (x.kind === 'fire') {
        const t = vessel(this.world, x.spec.target);
        const g = GUNS.find(([k]) => Number(k) === x.spec.weight)?.[1] ?? '';
        this.shots = { label: `${AMMO[x.spec.ammo].name}, ${g}, ${x.spec.range} m`, name: t?.name ?? '?', id: x.spec.target, t: this.world.time, hits: new Map(), hardest: 0, splashes: 0, killed: 0, wounded: 0, explosions: 0, fires: 0 };
      }
      this.actions.send(x);
    }
    this.timer = 0;
  }

  // ------------------------------------------------------------ every frame

  toggle(): boolean {
    if (this.isOpen) this.close();
    else this.open();
    return this.isOpen;
  }

  update(dt: number): void {
    this.trackMotion();
    if (!this.isOpen) return;
    this.timer -= dt;
    if (this.timer > 0) return;
    this.timer = 0.25;
    this.refresh();
  }

  /** Our ship's roll and pitch over the last 20 s, and seas she took in the last minute. */
  private trackMotion(): void {
    const w = this.world, t = w.time, s = w.player.ship;
    if (this.rolls.length && this.rolls[this.rolls.length - 1]!.t === t) return;
    this.rolls.push({ t, roll: Math.abs(s.heel), pitch: Math.abs(s.pitch) });
    while (this.rolls.length && this.rolls[0]!.t < t - 20) this.rolls.shift();
    while (this.seaEvents.length && this.seaEvents[0]!.t < t - 60) this.seaEvents.shift();
    let roll = 0, pitch = 0;
    for (const r of this.rolls) (roll = Math.max(roll, r.roll)), (pitch = Math.max(pitch, r.pitch));
    this.motion.roll = (roll * 180) / Math.PI;
    this.motion.pitch = (pitch * 180) / Math.PI;
    this.motion.slams = this.seaEvents.filter((e) => e.kind === 'slam').length;
    this.motion.green = this.seaEvents.filter((e) => e.kind === 'green').length;
  }

  private refresh(): void {
    const w = this.world;
    for (const { v, b } of this.speeds) b.classList.toggle('active', w.speed === v);
    this.refreshTargets();
    const c = this.ctx();
    for (const span of this.body.querySelectorAll<HTMLElement>('.dev-on')) {
      const t = `on ${c.target === w.player ? 'us' : c.target.name}`;
      if (span.textContent !== t) span.textContent = t;
    }
    for (const { tool, el: e } of this.lives) {
      const html = tool.live!(c, this);
      if (e.innerHTML !== html) e.innerHTML = html;
    }
    const insp = this.inspect(c.target);
    if (this.inspector.innerHTML !== insp) this.inspector.innerHTML = insp;
  }

  private refreshTargets(): void {
    const w = this.world, me = w.player;
    const list = w.vessels.filter((v) => v.alive);
    const sig = list.map((v) => `${v.id}:${v.state}`).join(',') + `|${this.pick}`;
    const s = this.targetSel;
    const label = (v: Vessel): string => {
      if (v === me) return `${v.name} (us)`;
      const d = Math.hypot(v.ship.com.x - me.ship.com.x, v.ship.com.z - me.ship.com.z);
      const state = v.state === 'active' ? '' : `, ${v.state}`;
      return `${v.name}: ${v.cls.name.toLowerCase()}, ${v.faction}${state}, ${d < 1000 ? `${Math.round(d)} m` : `${(d / 1852).toFixed(1)} nm`}${w.dev.spawned.has(v.id) ? ' ·test' : ''}`;
    };
    const auto = () => `Automatic: ${label(this.autoTarget())}`;
    if (sig !== this.listSig) {
      this.listSig = sig;
      s.innerHTML = '';
      const a = el('option', '', esc(auto()));
      a.value = '-2';
      s.append(a);
      for (const v of list) {
        const o = el('option', '', esc(label(v)));
        o.value = String(v.id);
        s.append(o);
      }
      s.value = list.some((v) => v.id === this.pick) ? String(this.pick) : '-2';
      if (s.value === '-2') this.pick = -2;
    } else if (document.activeElement !== s) {
      // Distances change all the time: relabel in place (never while it's open).
      const opts = s.options;
      if (opts[0]) opts[0].textContent = auto();
      for (let i = 1; i < opts.length; i++) {
        const v = list[i - 1];
        if (v && opts[i]!.textContent !== label(v)) opts[i]!.textContent = label(v);
      }
    }
  }

  /** The target at a glance, and what the last test did to the ship it was sent at. */
  private inspect(v: Vessel): string {
    const w = this.world, me = w.player;
    const d = v.damage, s = v.ship;
    const where = v === me ? 'our own ship' : (() => {
      const dist = Math.hypot(s.com.x - me.ship.com.x, s.com.z - me.ship.com.z);
      const rel = Math.atan2(s.com.x - me.ship.com.x, -(s.com.z - me.ship.com.z)) - me.ship.heading;
      return `${Math.round(dist)} m ${relWords(rel)}`;
    })();
    const orders = v === me ? '' : v.captain ? ` · ${v.captain.mode}${v.captain.forced ? ' (ordered)' : ''}` : ' · nobody in command';
    const hullRow = (side: 0 | 1) => [4, 3, 2, 1, 0].map((sec) => `<td>${bar(d.hull[sec]![side]! / d.hullMax)}</td>`).join('');
    const masts = Object.values(d.masts).map((m) => {
      const lost = m.lostLevel >= 3 ? 'gone' : `${pc(m.hp / m.max)}${m.lostLevel === 2 ? ', topmast gone' : m.lostLevel === 1 ? ', topgallant gone' : ''}`;
      return `${m.id} ${lost} (rig ${pc(m.rigging)})`;
    }).join(' · ');
    const guns = v.battery.guns.filter((g) => !g.destroyed).length;
    const fit = v.crew.members.filter((m) => m.alive && m.onBoard && m.health > 0.4).length + v.crew.reserve;
    const heel = (s.heel * 180) / Math.PI;
    let delta = '';
    const sn = this.snap;
    const was = sn && vessel(w, sn.id);
    if (sn && was) {
      const now = snapshot(was, w.time);
      const parts = [
        now.hull !== sn.hull ? `hull ${fmtDelta((now.hull - sn.hull) * 100, '%')}` : '',
        now.holes !== sn.holes ? `holes ${fmtDelta(now.holes - sn.holes)}` : '',
        Math.abs(now.leak - sn.leak) > 1e-3 ? `leak ${fmtDelta(now.leak - sn.leak, ' m²', 2)}` : '',
        now.killed !== sn.killed ? `killed ${fmtDelta(now.killed - sn.killed)}` : '',
        Math.abs(now.masts - sn.masts) > 1e-3 ? `masts ${fmtDelta((now.masts - sn.masts) * 100, '%')}` : '',
        Math.abs(now.sails - sn.sails) > 1e-3 ? `canvas ${fmtDelta((now.sails - sn.sails) * 100, '%')}` : '',
        now.guns !== sn.guns ? `guns ${fmtDelta(now.guns - sn.guns)}` : '',
        now.fires !== sn.fires ? `fires ${fmtDelta(now.fires - sn.fires)}` : '',
        Math.abs(now.flooding - sn.flooding) > 0.005 ? `flooding ${fmtDelta((now.flooding - sn.flooding) * 100, '%')}` : '',
      ].filter(Boolean);
      delta = `<div class="dev-delta">Since the last test on ${esc(was.name)} (${Math.round(w.time - sn.t)} s): ${parts.length ? parts.join(', ') : 'no change yet'}</div>`;
    }
    return [
      `<div class="dev-insp-head"><b>${esc(v.name)}</b> ${v.cls.name.toLowerCase()} · ${v.faction} · ${where}</div>`,
      `<div>${v.state}${orders} · ${(s.speed * KNOTS_PER_MS).toFixed(1)} kn · heel ${Math.abs(heel).toFixed(0)}°${Math.abs(heel) >= 0.5 ? (heel > 0 ? ' stbd' : ' port') : ''} · flooding ${pc(s.flooding)} · leak ${d.leakArea.toFixed(2)} m² · ${d.holes.length} holes · fires ${v.fire.burning}</div>`,
      `<table class="dev-hull"><tr><th></th><th>Bow</th><th>Fwd</th><th>Waist</th><th>Aft</th><th>Stern</th></tr><tr><th>Port</th>${hullRow(0)}</tr><tr><th>Stbd</th>${hullRow(1)}</tr></table>`,
      `<div>Masts: ${masts || 'none'}</div>`,
      `<div>Rudder ${pc(d.rudder)} · canvas ${pc(s.sails.sailHealth)} · guns ${guns}/${v.battery.guns.length} · people ${fit}/${v.crew.complement} fit, ${v.crew.fighters} on deck, morale ${pc(v.crew.morale)}, ${v.crew.killed} killed${d.brokenBack ? ' · back broken' : ''}${d.magazine.exploded ? ' · magazine gone up' : d.magazine.heat > 0.05 ? ` · magazine heat ${pc(d.magazine.heat)}` : ''}</div>`,
      delta,
    ].join('');
  }

  // ------------------------------------------------------------ the log

  handle(events: readonly SimEvent[]): void {
    const w = this.world;
    const name = (id: number) => (id < 0 ? 'the sea' : vessel(w, id)?.name ?? 'a ship');
    const hits = new Map<number, Map<string, number>>();
    const hurt = new Map<number, [number, number]>();
    const fires = new Map<number, number>();
    const lines: [string, string][] = [];
    const me = w.player.ship.com;
    const r = this.shots;
    const aim = r ? vessel(w, r.id) : undefined;
    for (const e of events) {
      switch (e.type) {
        case 'dev': lines.push([e.text, e.ok ? 'ok' : 'bad']); break;
        case 'impact': {
          const m = hits.get(e.vessel) ?? new Map<string, number>();
          m.set(e.what, (m.get(e.what) ?? 0) + 1);
          hits.set(e.vessel, m);
          if (r && e.vessel === r.id) {
            r.hits.set(e.what, (r.hits.get(e.what) ?? 0) + 1);
            r.hardest = Math.max(r.hardest, e.energy);
          }
          break;
        }
        case 'casualty': {
          const c = hurt.get(e.vessel) ?? [0, 0];
          c[e.killed ? 0 : 1]++;
          hurt.set(e.vessel, c);
          if (r && e.vessel === r.id) {
            if (e.killed) r.killed++;
            else r.wounded++;
          }
          break;
        }
        case 'fire':
          if (e.started) fires.set(e.vessel, (fires.get(e.vessel) ?? 0) + 1);
          if (r && e.started && e.vessel === r.id) r.fires++;
          break;
        case 'explosion':
          lines.push([`Explosion (${e.size.toFixed(1)}) ${e.vessel >= 0 ? `aboard ${name(e.vessel)}` : 'in the water'}.`, 'sim']);
          if (r && e.vessel === r.id) r.explosions++;
          break;
        case 'splash':
          if (r && aim && e.kind === 'ball' && Math.hypot(e.x - aim.ship.com.x, e.z - aim.ship.com.z) < 80) r.splashes++;
          break;
        case 'collapse': lines.push([`${e.what === 'keel' ? 'Her keel gives way: she has broken her back' : `Planking stove in (${e.what})`}.`, 'sim']); break;
        case 'mastFall': lines.push([`A ${e.mast} ${['', 'topgallant', 'topmast', 'mast'][e.level] ?? 'mast'} goes by the board.`, 'sim']); break;
        case 'collision': {
          let near = '', nd = Infinity;
          for (const v of w.vessels) {
            const d = Math.hypot(v.ship.com.x - e.x, v.ship.com.z - e.z);
            if (v !== w.player && d < nd) (nd = d), (near = v.name);
          }
          this.knocks.push({ t: w.time, energy: e.energy, near });
          if (this.knocks.length > 20) this.knocks.shift();
          lines.push([`Collision, energy ${e.energy.toFixed(2)}.`, 'sim']);
          break;
        }
        case 'slam':
        case 'washOver':
          if (Math.hypot(e.x - me.x, e.z - me.z) < 40) this.seaEvents.push({ t: w.time, kind: e.type === 'slam' ? 'slam' : 'green' });
          break;
        case 'boarding': lines.push([`Grapnels across: ${name(e.a)} and ${name(e.b)}.`, 'sim']); break;
        case 'captured': lines.push([`${name(e.vessel)} taken by ${name(e.by)}.`, 'sim']); break;
        case 'struck': lines.push([`${name(e.vessel)} strikes her colours.`, 'sim']); break;
        case 'shipSinking': lines.push([`${name(e.vessel)} is going down.`, 'sim']); break;
        case 'shipSunk': lines.push([`${name(e.vessel)} has sunk.`, 'sim']); break;
        default: break;
      }
    }
    for (const [id, m] of hits) lines.push([`Hit${[...m.values()].reduce((a, b) => a + b, 0) > 1 ? 's' : ''} on ${name(id)}: ${[...m.entries()].map(([k, n]) => (n > 1 ? `${k} ×${n}` : k)).join(', ')}.`, 'sim']);
    for (const [id, [k, h]] of hurt) lines.push([`${name(id)}: ${[k ? `${k} killed` : '', h ? `${h} wounded` : ''].filter(Boolean).join(', ')}.`, 'sim']);
    for (const [id, n] of fires) lines.push([`Fire${n > 1 ? ` in ${n} places` : ''} aboard ${name(id)}.`, 'sim']);
    for (const [text, cls] of lines) this.log(text, cls);
  }

  private log(text: string, cls: string): void {
    const line = el('div', cls);
    line.append(el('span', 't', clock(this.world.time)), document.createTextNode(text));
    if (cls !== 'sim' || !this.ticker.textContent) {
      this.ticker.textContent = text;
      this.ticker.className = `dev-ticker ${cls}${this.logEl.classList.contains('hidden') ? '' : ' hidden'}`;
    }
    const stick = this.logEl.scrollTop + this.logEl.clientHeight >= this.logEl.scrollHeight - 4;
    this.logEl.append(line);
    while (this.logEl.childElementCount > 120) this.logEl.firstElementChild!.remove();
    if (stick) this.logEl.scrollTop = this.logEl.scrollHeight;
  }
}

function fmtDelta(x: number, unit = '', digits = 0): string {
  const v = digits ? x.toFixed(digits) : String(Math.round(x));
  return `${x > 0 ? '+' : ''}${v}${unit}`;
}

/** Build every button's action with default parameters (tests use this to check each one is well formed). */
export function defaultActions(world: World): { category: string; tool: string; label: string; action: DevAction | DevAction[] | void }[] {
  const out: { category: string; tool: string; label: string; action: DevAction | DevAction[] | void }[] = [];
  const target = world.vessels.find((v) => v !== world.player) ?? world.player;
  const ctx: Ctx = { world, us: world.player, target, pair: () => [world.player.id, target.id], ui: { openChart: () => {}, restart: () => {} } };
  for (const c of CATEGORIES) {
    for (const t of c.tools) {
      const v: Values = {};
      for (const p of t.params ?? []) v[p.key] = p.value;
      for (const a of t.actions) out.push({ category: c.id, tool: t.title, label: a.label, action: a.run(v, ctx) });
    }
  }
  return out;
}
